import React, { useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PhoneCall, MessageCircle, Copy, PhoneOutgoing, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../lib/logger';

export const CALL_RESULTS = ['Connected', 'Not Connected', 'Call Back Later', 'Wrong Number'];
export const WHATSAPP_TYPES = ['Intro', 'Follow-Up', 'Reminder', 'Closing', 'Support'];

const DEFAULT_TEMPLATE = "Hi [Name], this is [Rep] from Futurists. I'm reaching out regarding your enquiry.";
let cachedTemplate = null;

export const normalizePhone = (phone) => {
  if (!phone) return '';
  let digits = String(phone).replace(/[^0-9]/g, '');
  while (digits.startsWith('0')) digits = digits.slice(1);
  if (digits.length === 10) digits = `91${digits}`;
  return digits;
};

const fetchTemplate = async () => {
  if (cachedTemplate) return cachedTemplate;
  try {
    const { data } = await api.get('/settings');
    cachedTemplate = data.whatsapp_template || DEFAULT_TEMPLATE;
  } catch (e) {
    logger.warn('Falling back to default WhatsApp template', e);
    cachedTemplate = DEFAULT_TEMPLATE;
  }
  return cachedTemplate;
};

export const resetTemplateCache = () => { cachedTemplate = null; };

const resolveTemplate = (tpl, lead, repName) => (tpl || DEFAULT_TEMPLATE)
  .replaceAll('[Name]', (lead.lead_name || '').split(' ')[0] || 'there')
  .replaceAll('[Rep]', repName || 'the team')
  .replaceAll('[Company]', lead.company_name || 'your company');

const DialActions = ({ digits, onCopy }) => (
  <>
    <div className="flex gap-2">
      <Button asChild className="flex-1 h-9" data-testid="open-dialer-btn">
        <a href={`tel:+${digits}`}><PhoneOutgoing size={14} className="mr-1.5" /> Open Dialer</a>
      </Button>
      <Button variant="outline" className="h-9" onClick={onCopy} data-testid="copy-number-btn">
        <Copy size={13} className="mr-1.5" /> Copy Number
      </Button>
    </div>
    <div className="text-[11px] text-muted-foreground -mt-1">Mobile opens your dialer directly · desktop uses your softphone / VoIP app (or copy the number).</div>
  </>
);

const CallOutcomeLogger = ({ duration, setDuration, note, setNote, saving, onLog }) => (
  <div className="space-y-2 rounded-lg border bg-muted/40 p-3">
    <div className="text-xs font-semibold">Log the outcome</div>
    <div className="flex gap-2">
      <div className="flex-1 space-y-1">
        <Label className="text-[11px]">Duration (min)</Label>
        <Input type="number" min="0" step="0.5" placeholder="optional" value={duration} onChange={(e) => setDuration(e.target.value)} className="h-8 text-xs" data-testid="call-duration-input" />
      </div>
      <div className="flex-[2] space-y-1">
        <Label className="text-[11px]">Note</Label>
        <Input placeholder="optional" value={note} onChange={(e) => setNote(e.target.value)} className="h-8 text-xs" data-testid="call-note-input" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-1.5">
      {CALL_RESULTS.map((r) => (
        <Button key={r} variant="outline" size="sm" className="h-8 text-xs justify-start" disabled={!!saving}
          onClick={() => onLog(r)} data-testid={`call-result-${r.toLowerCase().replace(/\s+/g, '-')}`}>
          {saving === r ? <Loader2 size={12} className="animate-spin mr-1" /> : null}{r}
        </Button>
      ))}
    </div>
  </div>
);

const CallDialog = ({ lead, open, onClose, onLogged }) => {
  const [duration, setDuration] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(null);
  const digits = normalizePhone(lead.phone);

  const copyNumber = () => navigator.clipboard.writeText(lead.phone || '').then(
    () => toast.success('Number copied — dial from your calling app'),
    () => toast.error('Copy failed')
  );

  const logResult = async (result) => {
    setSaving(result);
    try {
      const { data } = await api.post(`/leads/${lead.id}/comm`, {
        channel: 'call',
        result,
        duration_minutes: duration ? Number(duration) : null,
        note: note || null,
      });
      toast.success(`Call logged — ${result}. Last touch updated.`);
      onLogged?.(data.lead);
      setDuration(''); setNote('');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log call');
    } finally { setSaving(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="call-dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-base">
            <PhoneCall size={16} className="text-primary" /> Call {lead.lead_name}
          </DialogTitle>
          <DialogDescription className="text-xs">{lead.phone} {lead.company_name ? `· ${lead.company_name}` : ''}</DialogDescription>
        </DialogHeader>
        <DialActions digits={digits} onCopy={copyNumber} />
        <CallOutcomeLogger duration={duration} setDuration={setDuration} note={note} setNote={setNote} saving={saving} onLog={logResult} />
      </DialogContent>
    </Dialog>
  );
};

const WhatsAppComposer = ({ msgType, setMsgType, message, setMessage, ready }) => (
  <>
    <div className="space-y-1">
      <Label className="text-[11px]">Message Type</Label>
      <Select value={msgType} onValueChange={setMsgType}>
        <SelectTrigger className="h-8 text-xs" data-testid="whatsapp-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {WHATSAPP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1">
      <Label className="text-[11px]">Message</Label>
      <Textarea rows={4} value={ready ? message : 'Loading template…'} disabled={!ready}
        onChange={(e) => setMessage(e.target.value)} className="text-xs" data-testid="whatsapp-message-input" />
      <div className="text-[10.5px] text-muted-foreground">Template editable in Settings — placeholders: [Name], [Rep], [Company]</div>
    </div>
  </>
);

const WhatsAppDialog = ({ lead, open, onClose, onLogged }) => {
  const { user } = useAuth();
  const [msgType, setMsgType] = useState('Intro');
  const [message, setMessage] = useState('');
  const [ready, setReady] = useState(false);
  const [sending, setSending] = useState(false);
  const digits = normalizePhone(lead.phone);

  React.useEffect(() => {
    if (open) {
      setReady(false);
      fetchTemplate().then((tpl) => {
        setMessage(resolveTemplate(tpl, lead, user?.name));
        setReady(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead.id]);

  const sendAndLog = async () => {
    setSending(true);
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
    try {
      const { data } = await api.post(`/leads/${lead.id}/comm`, {
        channel: 'whatsapp',
        message_type: msgType,
        message,
      });
      toast.success(`WhatsApp ${msgType} logged. Last touch updated.`);
      onLogged?.(data.lead);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log WhatsApp');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="whatsapp-dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-base">
            <MessageCircle size={16} className="text-emerald-600" /> WhatsApp {lead.lead_name}
          </DialogTitle>
          <DialogDescription className="text-xs">{lead.phone} · opens wa.me on mobile & desktop</DialogDescription>
        </DialogHeader>
        <WhatsAppComposer msgType={msgType} setMsgType={setMsgType} message={message} setMessage={setMessage} ready={ready} />
        <Button className="w-full h-9 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={sendAndLog} disabled={!ready || sending} data-testid="whatsapp-send-btn">
          {sending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Send size={13} className="mr-1.5" />}
          Open WhatsApp & Log
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const COMM_DISABLED_CLASSES = 'bg-muted border-border text-muted-foreground/50 cursor-not-allowed';

const FOLLOWUP_NUDGE_TEMPLATE = (lead, repName) =>
  `Hi ${(lead.lead_name || '').split(' ')[0] || 'there'}, this is ${repName || 'the team'} from Futurists — just following up on our conversation${lead.company_name ? ` about ${lead.company_name}` : ''}. Is there a good time today to connect?`;

/** One-tap WhatsApp follow-up: opens wa.me with a pre-filled nudge message and
 * logs the touch automatically (refreshes last_touch, clearing the 48h+ badge). */
export const QuickWhatsAppButton = ({ lead, onLogged }) => {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const digits = normalizePhone(lead.phone);

  const send = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!digits) { toast.error('No phone number on this lead'); return; }
    if (sending) return;
    setSending(true);
    const message = FOLLOWUP_NUDGE_TEMPLATE(lead, user?.name);
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
    try {
      const { data } = await api.post(`/leads/${lead.id}/comm`, {
        channel: 'whatsapp', message_type: 'Follow-Up', message,
      });
      toast.success('WhatsApp follow-up opened & logged — last touch updated');
      onLogged?.(data.lead);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log WhatsApp follow-up');
    } finally { setSending(false); }
  };

  return (
    <button
      onClick={send}
      title={digits ? 'One-tap WhatsApp follow-up (auto-logged)' : 'No phone number'}
      aria-label={`WhatsApp follow-up to ${lead.lead_name}`}
      data-testid={`nudge-whatsapp-btn-${lead.id}`}
      className={`inline-flex items-center gap-1 h-[18px] px-1.5 rounded-full border text-[10px] font-medium transition-colors ${
        digits
          ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:border-emerald-600 hover:text-white'
          : COMM_DISABLED_CLASSES
      }`}
    >
      {sending ? <Loader2 size={10} className="animate-spin" /> : <MessageCircle size={10} />}
      Follow up
    </button>
  );
};

const CommIconButton = ({ hasPhone, onClick, title, ariaLabel, testId, activeClasses, children }) => (
  <button onClick={onClick} title={title} aria-label={ariaLabel} data-testid={testId}
    className={`h-7 w-7 rounded-md border flex items-center justify-center transition-colors ${hasPhone ? activeClasses : COMM_DISABLED_CLASSES}`}>
    {children}
  </button>
);

export const CommActions = ({ lead, onLogged, showPhone = false, className = '' }) => {
  const [dialog, setDialog] = useState(null); // 'call' | 'whatsapp'
  const hasPhone = !!normalizePhone(lead.phone);

  const openDialog = (e, which) => {
    e.stopPropagation();
    e.preventDefault();
    if (!hasPhone) { toast.error('No phone number on this lead'); return; }
    setDialog(which);
  };

  return (
    <div className={`flex items-center gap-1 ${className}`} onClick={(e) => e.stopPropagation()}>
      {showPhone && <span className="text-[11px] text-muted-foreground mr-1 truncate">{lead.phone || 'No phone'}</span>}
      <CommIconButton hasPhone={hasPhone} onClick={(e) => openDialog(e, 'call')}
        title={hasPhone ? `Call ${lead.phone}` : 'No phone number'} ariaLabel={`Call ${lead.lead_name}`}
        testId={`call-btn-${lead.id}`}
        activeClasses="bg-primary/5 border-primary/25 text-primary hover:bg-primary hover:text-primary-foreground">
        <PhoneCall size={13} />
      </CommIconButton>
      <CommIconButton hasPhone={hasPhone} onClick={(e) => openDialog(e, 'whatsapp')}
        title={hasPhone ? `WhatsApp ${lead.phone}` : 'No phone number'} ariaLabel={`WhatsApp ${lead.lead_name}`}
        testId={`whatsapp-btn-${lead.id}`}
        activeClasses="bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-600 hover:text-white">
        <MessageCircle size={13} />
      </CommIconButton>
      {dialog === 'call' && <CallDialog lead={lead} open onClose={() => setDialog(null)} onLogged={onLogged} />}
      {dialog === 'whatsapp' && <WhatsAppDialog lead={lead} open onClose={() => setDialog(null)} onLogged={onLogged} />}
    </div>
  );
};
